import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { XeroInvoice } from '../../../types/xero';
import { parseXeroDate } from '../../../utils/xeroDate';

interface ReceivablesAgingViewProps {
  invoices: XeroInvoice[];
  periodLabel?: string;
}

interface AgingBucket {
  label: string;
  shortLabel: string;
  amount: number;
  count: number;
  color: string;
  invoices: { name: string; contact: string; amount: number; daysOverdue: number }[];
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

export function ReceivablesAgingView({ invoices, periodLabel }: ReceivablesAgingViewProps) {
  const { buckets, totalOutstanding, totalOverdue, overdueCount } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const agingBuckets: AgingBucket[] = [
      { label: 'Current (not yet due)', shortLabel: 'Current', amount: 0, count: 0, color: '#50CD89', invoices: [] },
      { label: '1-30 days overdue', shortLabel: '1-30d', amount: 0, count: 0, color: '#F6C000', invoices: [] },
      { label: '31-60 days overdue', shortLabel: '31-60d', amount: 0, count: 0, color: '#FFA800', invoices: [] },
      { label: '61-90 days overdue', shortLabel: '61-90d', amount: 0, count: 0, color: '#F1416C', invoices: [] },
      { label: '90+ days overdue', shortLabel: '90d+', amount: 0, count: 0, color: '#D9214E', invoices: [] },
    ];

    let outstanding = 0;
    let overdue = 0;
    let overdueN = 0;

    invoices.forEach((inv) => {
      if (inv.Status !== 'AUTHORISED' || inv.AmountDue <= 0) return;
      outstanding += inv.AmountDue;

      const dueDate = parseXeroDate((inv as any).DueDate || (inv as any).DueDateString);
      if (!dueDate) return;

      const diffMs = now.getTime() - dueDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      const entry = {
        name: inv.InvoiceNumber,
        contact: inv.Contact?.Name || 'Unknown',
        amount: inv.AmountDue,
        daysOverdue: Math.max(0, diffDays),
      };

      let bucketIdx: number;
      if (diffDays <= 0) {
        bucketIdx = 0;
      } else if (diffDays <= 30) {
        bucketIdx = 1;
        overdue += inv.AmountDue;
        overdueN++;
      } else if (diffDays <= 60) {
        bucketIdx = 2;
        overdue += inv.AmountDue;
        overdueN++;
      } else if (diffDays <= 90) {
        bucketIdx = 3;
        overdue += inv.AmountDue;
        overdueN++;
      } else {
        bucketIdx = 4;
        overdue += inv.AmountDue;
        overdueN++;
      }

      agingBuckets[bucketIdx].amount += inv.AmountDue;
      agingBuckets[bucketIdx].count++;
      agingBuckets[bucketIdx].invoices.push(entry);
    });

    agingBuckets.forEach(b => b.invoices.sort((a, c) => c.amount - a.amount));

    return { buckets: agingBuckets, totalOutstanding: outstanding, totalOverdue: overdue, overdueCount: overdueN };
  }, [invoices]);

  const activeBuckets = buckets.filter(b => b.amount > 0);

  const formatCompact = (amount: number) => {
    if (Math.abs(amount) >= 1000) {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', notation: 'compact', maximumFractionDigits: 1 }).format(amount);
    }
    return formatCurrency(amount);
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit',
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: '65%',
        distributed: true,
      },
    },
    colors: activeBuckets.map(b => b.color),
    dataLabels: {
      enabled: true,
      formatter: (val) => formatCurrency(val as number),
      style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] },
      offsetX: 5,
    },
    xaxis: {
      labels: {
        formatter: (val) => formatCompact(Number(val)),
        style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        style: { colors: 'var(--bs-gray-700)', fontSize: '12px' },
        maxWidth: 140,
      },
    },
    legend: { show: false },
    tooltip: {
      theme: 'dark',
      custom: ({ dataPointIndex }) => {
        const b = activeBuckets[dataPointIndex];
        if (!b) return '';
        return `<div class="px-3 py-2">
          <div class="fw-bold mb-1">${b.label}</div>
          <div>Outstanding: ${formatCurrency(b.amount)}</div>
          <div class="text-muted fs-8">${b.count} invoice${b.count !== 1 ? 's' : ''}</div>
        </div>`;
      },
    },
    grid: {
      borderColor: 'var(--bs-gray-200)',
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.9 } },
      active: { filter: { type: 'none' } },
    },
  };

  const chartSeries = [{
    name: 'Outstanding',
    data: activeBuckets.map(b => Math.round(b.amount * 100) / 100),
  }];

  if (totalOutstanding === 0) {
    return (
      <div className="text-center py-10">
        <i className="bi bi-check-circle-fill text-success fs-2x d-block mb-3" />
        <span className="text-muted fs-6">No outstanding invoices</span>
      </div>
    );
  }

  return (
    <div className="row g-4">
      {/* Left summary */}
      <div className="col-12 col-lg-4">
        <div className="d-flex flex-column gap-4">
          <div>
            <span className="text-muted fs-7 d-block mb-1">Total Outstanding</span>
            <div className="fs-2 fw-bold text-gray-800">{formatCurrency(totalOutstanding)}</div>
          </div>
          {totalOverdue > 0 && (
            <div>
              <span className="text-muted fs-7 d-block mb-1">Overdue</span>
              <div className="fs-3 fw-bold text-danger">{formatCurrency(totalOverdue)}</div>
              <span className="text-muted fs-8">{overdueCount} invoice{overdueCount !== 1 ? 's' : ''} past due</span>
            </div>
          )}
          <div className="pt-2 border-top">
            <span className="text-muted fs-8 d-block mb-2">Aging Breakdown</span>
            <div className="d-flex flex-column gap-2">
              {buckets.map((b) => (
                <div key={b.label} className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <span className="bullet bullet-dot" style={{ width: 8, height: 8, backgroundColor: b.color }} />
                    <span className="fs-8 text-gray-700">{b.shortLabel}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-8 fw-semibold text-gray-800">
                      {b.amount > 0 ? formatCurrency(b.amount) : '-'}
                    </span>
                    {b.count > 0 && (
                      <span className="badge badge-light-secondary fs-9">{b.count}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal bar chart + top overdue */}
      <div className="col-12 col-lg-8">
        <Chart
          options={{ ...chartOptions, xaxis: { ...chartOptions.xaxis, categories: activeBuckets.map(b => b.shortLabel) } }}
          series={chartSeries}
          type="bar"
          height={Math.max(180, activeBuckets.length * 40)}
        />
        {totalOverdue > 0 && (
          <div className="mt-3">
            <span className="text-muted fs-8 d-block mb-2">Largest Overdue</span>
            <div className="table-responsive" style={{ maxHeight: '120px' }}>
              <table className="table table-sm table-row-bordered mb-0">
                <tbody>
                  {buckets
                    .flatMap(b => b.invoices.filter(inv => inv.daysOverdue > 0))
                    .sort((a, b) => b.amount - a.amount)
                    .slice(0, 5)
                    .map((inv, idx) => (
                      <tr key={idx} className="fs-8">
                        <td className="fw-semibold">{inv.name}</td>
                        <td className="text-muted">{inv.contact}</td>
                        <td className="text-end text-danger fw-semibold">{formatCurrency(inv.amount)}</td>
                        <td className="text-end text-muted">{inv.daysOverdue}d</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReceivablesAgingView;
